import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import { EditIcon, RemoveIcon } from '@common/bw-ui/icons';

export interface BasketItemCardItem {
  id: string;
  type: 'booking' | 'event' | 'contract' | 'product' | 'course';
  name: string;
  subtitle?: string;
  price: number;
  discountLabels?: string[];
  creditLabel?: string;
  isLoadingPrice?: boolean;
  image?: string;
  details?: string[];
  editable?: boolean;
}

export interface BasketItemCardProps {
  item: BasketItemCardItem;
  onEdit?: (id: string) => void;
  onRemove: (id: string) => void;
  className?: string;
}

function formatPrice(price: number): string {
  return `£${price.toFixed(2)}`;
}

function ItemActions({
  itemId,
  onEdit,
  onRemove,
}: {
  itemId: string;
  onEdit?: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      {onEdit && (
        <button
          type="button"
          data-slot="edit-action"
          onClick={() => onEdit(itemId)}
          className="tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-0"
        >
          <EditIcon className="tw:w-3.5 tw:h-3.5" aria-hidden="true" />
          <span>Edit</span>
        </button>
      )}
      <button
        type="button"
        data-slot="remove-action"
        onClick={() => onRemove(itemId)}
        className="tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-destructive tw:bg-transparent tw:border-none tw:cursor-pointer tw:p-0"
      >
        <RemoveIcon className="tw:w-3.5 tw:h-3.5" aria-hidden="true" />
        <span>Remove</span>
      </button>
    </div>
  );
}

export function BasketItemCard({
  item,
  onEdit,
  onRemove,
  className,
}: BasketItemCardProps) {
  return (
    <div
      data-slot="item"
      className={cn(
        'tw:grid tw:gap-x-3 tw:gap-y-1',
        'tw:grid-cols-[minmax(0,1fr)_auto]',
        'tw:sm:[&:has(>[data-slot=image])]:grid-cols-[4rem_minmax(0,1fr)_auto]',
        className,
      )}
    >
      {item.image && (
        <div
          data-slot="image"
          className="tw:hidden tw:sm:block tw:row-span-3 tw:md:row-span-2 tw:w-16 tw:h-16 tw:rounded-lg tw:overflow-hidden"
        >
          <img
            src={item.image}
            alt={item.name}
            className="tw:w-full tw:h-full tw:object-cover"
          />
        </div>
      )}

      <h3
        data-slot="title"
        className="tw:text-base tw:md:text-lg tw:text-foreground tw:font-medium tw:col-start-1 tw:sm:[div:has(>[data-slot=image])>&]:col-start-2"
      >
        {item.name}
      </h3>

      <span
        data-slot="price"
        className={cn(
          'tw:text-base tw:text-foreground tw:font-medium tw:text-right tw:col-start-2 tw:sm:[div:has(>[data-slot=image])>&]:col-start-3',
          item.isLoadingPrice && 'tw:animate-pulse tw:text-muted',
        )}
      >
        {formatPrice(item.price)}
      </span>

      {item.subtitle && (
        <p
          data-slot="subtitle"
          className="tw:text-sm tw:text-muted tw:font-normal tw:col-start-1 tw:sm:[div:has(>[data-slot=image])>&]:col-start-2"
        >
          {item.subtitle}
        </p>
      )}

      {item.details && item.details.length > 0 && (
        <ul
          data-slot="details"
          className="tw:text-sm tw:text-muted tw:font-normal tw:col-start-1 tw:sm:[div:has(>[data-slot=image])>&]:col-start-2 tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-0.5"
        >
          {item.details.map((detail, index) => (
            <li key={index + detail}>{detail}</li>
          ))}
        </ul>
      )}

      {item.discountLabels && item.discountLabels.length > 0 && (
        <div
          data-slot="discount"
          className={cn(
            'tw:flex tw:flex-col tw:gap-0.5 tw:col-start-1 tw:sm:[div:has(>[data-slot=image])>&]:col-start-2',
            item.isLoadingPrice && 'tw:animate-pulse',
          )}
        >
          {item.discountLabels.map((label, i) => (
            <p
              key={i}
              className="tw:text-sm tw:text-success tw:font-medium tw:m-0"
            >
              {label}
            </p>
          ))}
        </div>
      )}
      {item.creditLabel && (
        <p
          data-slot="credit"
          className={cn(
            'tw:text-sm tw:text-info tw:font-medium tw:col-start-1 tw:sm:[div:has(>[data-slot=image])>&]:col-start-2',
            item.isLoadingPrice && 'tw:animate-pulse',
          )}
        >
          {item.creditLabel}
        </p>
      )}
      <div
        data-slot="actions"
        className="tw:col-start-1 tw:sm:col-start-2 tw:sm:[div:has(>[data-slot=image])>&]:col-start-3 tw:md:row-start-2 tw:sm:justify-self-end tw:mt-1 tw:md:mt-0"
      >
        <ItemActions
          itemId={item.id}
          onEdit={item.editable !== false ? onEdit : undefined}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
}
